import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSelectTheme } from "@/contexts/SelectThemeContext";
import { Truck, RefreshCw, AlertCircle } from "lucide-react";

/**
 * ShippingRatePicker — real-time USPS + UPS rate quotes at checkout.
 *
 * Backend: POST /shipping/quotes (see routes/shipping.py).
 *
 * Props:
 *   items:        cart line items (product_id + quantity) — used server-side
 *                 for parcel weight estimation
 *   destination:  { zip, street1?, city?, state? } — refetch triggers on
 *                 change of the ZIP (5-digit valid ZIPs only)
 *   disabled:     hide the picker entirely (e.g. local-delivery is on)
 *   onSelect:     called with { quote_id, option_id, rate_cents,
 *                 service_display, carrier, delivery_days } — or `null`
 *                 when the picker resets
 *
 * Behavior:
 *   • Only fetches when ZIP is 5+ digits, valid US pattern.
 *   • Debounces destination changes by 400ms so we don't hammer Shippo
 *     while the buyer is still typing.
 *   • On 503 (Shippo not configured) → hides itself gracefully. Legacy
 *     flat-rate + free-threshold logic in useCart/Checkout stays authoritative.
 *   • On 422 (invalid address) → shows the exact error inline.
 */
export default function ShippingRatePicker({ items, destination, disabled, onSelect, freeShippingOnGround }) {
  const [state, setState] = useState({ loading: false, options: null, quoteId: null, error: null, unavailable: false });
  const [selected, setSelected] = useState(null); // option_id
  const lastFetchKey = useRef("");
  // iter156.5.2 — tier-aware palette so the picker doesn't render invisible
  // charcoal labels on the R-1617 dark checkout shell. Reads the same
  // `SelectThemeContext` that Checkout flips via `useSetSelectTheme` on mount.
  const { isSelect } = useSelectTheme();

  // iter221 — owner directive 2026-10-12: free shipping applies to USPS
  // Priority Mail AND UPS 2nd Day Air only. The prop name is preserved for
  // minimal churn across callers but the predicate below is the source of
  // truth. Ground remains a paid option.
  const isFreeEligibleService = useCallback(
    (opt) => opt?.service === "usps_priority" || opt?.service === "ups_second_day_air",
    []
  );

  const fetchRates = useCallback(async (dest) => {
    const key = JSON.stringify({ z: dest.zip, s: dest.state, c: dest.city, l: dest.street1, items: items.map(i => `${i.product_id}:${i.quantity}`) });
    if (key === lastFetchKey.current) return;
    lastFetchKey.current = key;

    setState((s) => ({ ...s, loading: true, error: null }));
    setSelected(null);
    onSelect?.(null);

    try {
      const { data } = await api.post("/shipping/quotes", {
        destination: {
          zip: dest.zip,
          street1: dest.street1 || undefined,
          city: dest.city || undefined,
          state: dest.state || undefined,
          country: "US",
        },
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setState({ loading: false, options: data.options, quoteId: data.quote_id, error: null, unavailable: false });
      // Auto-select the cheapest option so the buyer sees an initial total
      if (data.options?.length) {
        const cheapest = data.options.reduce((a, b) => (a.rate_cents <= b.rate_cents ? a : b));
        setSelected(cheapest.option_id);
        onSelect?.({
          quote_id: data.quote_id,
          option_id: cheapest.option_id,
          rate_cents: cheapest.rate_cents,
          service: cheapest.service,                 // iter221 — lowercase Shippo token; parent needs it for free-ship eligibility
          service_display: cheapest.service_display,
          carrier: cheapest.carrier,
          delivery_days: cheapest.delivery_days,
        });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load shipping rates.";
      // 503 = Shippo not configured. Hide the whole picker — the legacy
      // flat-rate/local flow in the parent Checkout will take over.
      if (status === 503) {
        setState({ loading: false, options: null, quoteId: null, error: null, unavailable: true });
      } else {
        setState({ loading: false, options: null, quoteId: null, error: detail, unavailable: false });
      }
    }
  }, [items, onSelect]);

  // Debounced refetch when destination changes.
  useEffect(() => {
    if (disabled) { onSelect?.(null); return; }
    if (!destination?.zip || !/^\d{5}(-\d{4})?$/.test(destination.zip)) {
      lastFetchKey.current = "";
      setState((s) => ({ ...s, options: null, error: null }));
      setSelected(null);
      onSelect?.(null);
      return;
    }
    const t = setTimeout(() => { fetchRates(destination); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination?.zip, destination?.state, destination?.city, destination?.street1, disabled]);

  const pick = (opt) => {
    setSelected(opt.option_id);
    onSelect?.({
      quote_id: state.quoteId,
      option_id: opt.option_id,
      rate_cents: opt.rate_cents,
      service: opt.service,                       // iter221 — lowercase Shippo token; parent needs it for free-ship eligibility
      service_display: opt.service_display,
      carrier: opt.carrier,
      delivery_days: opt.delivery_days,
    });
  };

  if (disabled) return null;
  if (state.unavailable) return null; // hidden — parent falls back to flat rate

  return (
    <section className="mb-10" data-testid="shipping-rate-picker">
      <div className="flex items-center gap-2 mb-4">
        <Truck size={18} strokeWidth={1.7} className={isSelect ? "text-[#6E521A]" : "text-oxblood"} />
        <h2 className={`font-serif text-[22px] md:text-[26px] ${isSelect ? "text-[#1B1614]" : "text-charcoal"}`}>Shipping method</h2>
      </div>

      {!destination?.zip || !/^\d{5}(-\d{4})?$/.test(destination.zip) ? (
        <p className={`text-[15px] border border-dashed p-4 rounded-md ${isSelect ? "text-[#1B1614]/70 border-[#D6C8A3]" : "text-charcoal/85 border-borderSubtle"}`} data-testid="shipping-picker-need-zip">
          Enter a shipping ZIP above to see live USPS and UPS rates.
        </p>
      ) : state.loading ? (
        <div className={`flex items-center gap-2 text-[15px] ${isSelect ? "text-[#1B1614]/70" : "text-charcoal/85"}`} data-testid="shipping-picker-loading">
          <RefreshCw size={14} className="animate-spin" /> Fetching rates…
        </div>
      ) : state.error ? (
        <div className={`flex items-start gap-2 p-4 rounded-md border ${isSelect ? "border-[#DDA94E]/40 bg-[#DDA94E]/[0.06]" : "border-oxblood/40 bg-oxblood/5"}`} data-testid="shipping-picker-error">
          <AlertCircle size={16} className={`mt-0.5 shrink-0 ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`} />
          <div>
            <p className={`text-[15px] font-medium ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`}>{state.error}</p>
            <button
              type="button"
              onClick={() => { lastFetchKey.current = ""; fetchRates(destination); }}
              className={`mt-2 text-[13px] uppercase tracking-[0.22em] font-mono ${isSelect ? "text-[#1B1614] hover:text-[#6E521A]" : "text-charcoal hover:text-oxblood"}`}
              data-testid="shipping-picker-retry"
            >
              Retry
            </button>
          </div>
        </div>
      ) : state.options?.length ? (
        <div className="space-y-2" data-testid="shipping-picker-options">
          {state.options.map((opt) => {
            const groundFree = freeShippingOnGround && isFreeEligibleService(opt);
            return (
            <label
              key={opt.option_id}
              className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer transition-colors ${
                selected === opt.option_id
                  ? (isSelect ? "border-[#DDA94E] bg-[#DDA94E]/[0.08]" : "border-oxblood bg-oxblood/5")
                  : (isSelect ? "border-[#D6C8A3] bg-[#EBE0BE]/60 hover:border-[#DDA94E] hover:bg-[#DDA94E]/[0.12]" : "border-borderSubtle hover:border-charcoal")
              }`}
              data-testid={`shipping-option-${opt.carrier}-${opt.service}`}
            >
              <input
                type="radio"
                name="shipping_option"
                value={opt.option_id}
                checked={selected === opt.option_id}
                onChange={() => pick(opt)}
                className={`w-4 h-4 shrink-0 ${isSelect ? "accent-[#DDA94E]" : "accent-oxblood"}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-[15px] font-medium ${isSelect ? "text-[#1B1614]" : "text-charcoal"}`}>{opt.service_display}</p>
                  {groundFree && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-mono font-bold rounded-full ${isSelect ? "bg-[#DDA94E]/15 text-[#6E521A] border border-[#DDA94E]/50" : "bg-[#EDF7EE] text-[#265427] border border-[#B7DCB9]"}`}
                      data-testid={`shipping-option-free-${opt.carrier}-${opt.service}`}
                    >
                      Free
                    </span>
                  )}
                </div>
                {opt.delivery_days != null && (
                  <p className={`text-[13px] ${isSelect ? "text-[#1B1614]/70" : "text-charcoal/85"}`}>Est. {opt.delivery_days} business day{opt.delivery_days === 1 ? "" : "s"}</p>
                )}
              </div>
              {groundFree ? (
                <span className={`font-mono font-semibold text-[15px] whitespace-nowrap ${isSelect ? "text-[#6E521A]" : "text-[#2F7A31]"}`}>
                  <span className={`line-through mr-2 ${isSelect ? "text-[#1B1614]/40" : "text-charcoal/40"}`}>${(opt.rate_cents / 100).toFixed(2)}</span>
                  FREE
                </span>
              ) : (
                <span className={`font-mono font-semibold text-[15px] whitespace-nowrap ${isSelect ? "text-[#1B1614]" : "text-charcoal"}`}>${(opt.rate_cents / 100).toFixed(2)}</span>
              )}
            </label>
            );
          })}
          <p className={`text-[12px] mt-3 ${isSelect ? "text-[#1B1614]/60" : "text-charcoal/85"}`}>
            Rates are live from USPS and UPS. Quote expires in 15 min.
          </p>
        </div>
      ) : null}
    </section>
  );
}
